import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Globe, Menu, X } from 'lucide-react';
import { BrandLogoWithWordmark } from './BrandLogo';
import { getMobilePrimaryLinks, getRoleLinks } from './layout/navConfig';

const LANGUAGES = [
  { code: 'ru', label: 'RU' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();

  return (
    <div className={`flex items-center gap-1 ${compact ? '' : 'px-3 py-2'}`}>
      {!compact && <Globe size={16} className="text-text-muted mr-1" strokeWidth={1.5} />}
      {LANGUAGES.map(({ code, label }) => (
        <button
          key={code}
          onClick={() => i18n.changeLanguage(code)}
          title={label}
          className={`text-xs font-medium px-2 py-1 rounded transition-all ${
            i18n.language === code
              ? 'bg-primary text-white'
              : 'text-text-muted hover:text-primary hover:bg-primary/10'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

export const MobileNav = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const primaryLinks = useMemo(() => getMobilePrimaryLinks(user?.role, t), [user?.role, t]);
  const allLinks = useMemo(() => getRoleLinks(user?.role, t), [user?.role, t]);
  const settingsPath = user?.role === 'athlete' ? '/notifications' : '/profile';

  const go = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login');
  };

  return (
    <>
      <div className="mobile-nav">
        {primaryLinks.map(({ to, icon: Icon, label }) => (
          <button
            key={to}
            onClick={() => navigate(to)}
            data-testid={`mobile-nav-${label.toLowerCase()}`}
            className={`flex flex-col items-center gap-1 ${
              location.pathname === to ? 'text-primary' : 'text-text-muted'
            }`}
          >
            <Icon size={22} strokeWidth={1.5} />
            <span className="text-[11px] font-medium">{label}</span>
          </button>
        ))}

        <button
          onClick={() => setMenuOpen(true)}
          data-testid="mobile-nav-more"
          className="flex flex-col items-center gap-1 text-text-muted"
        >
          <Menu size={22} strokeWidth={1.5} />
          <span className="text-[11px] font-medium">{t('nav.more')}</span>
        </button>
      </div>
      {menuOpen && (
        <div className="fixed inset-0 z-[60]">
          <button
            className="absolute inset-0 bg-black/40"
            onClick={() => setMenuOpen(false)}
            aria-label={t('common.close')}
          />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl border-t border-slate-200 p-4 pb-8 max-h-[75vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BrandLogoWithWordmark size={24} className="brand-logo-mobile-menu" />
                <h3 className="text-sm font-medium text-text-primary">{t('nav.menu')}</h3>
              </div>
              <button onClick={() => setMenuOpen(false)} className="p-2 text-text-muted hover:text-primary">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <button onClick={() => go('/profile')} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">
                {t('nav.profile')}
              </button>
              <button onClick={() => go(settingsPath)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">
                {t('nav.settings')}
              </button>
              <button onClick={handleLogout} className="text-xs px-3 py-2 rounded-md border border-red-200 text-red-600 hover:bg-red-50">
                {t('nav.logout')}
              </button>
            </div>

            <div className="space-y-1">
              {allLinks.map(({ to, icon: Icon, label }) => (
                <button
                  key={to}
                  onClick={() => go(to)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left ${
                    location.pathname === to
                      ? 'bg-primary text-white'
                      : 'text-text-secondary hover:bg-slate-50'
                  }`}
                >
                  <Icon size={18} strokeWidth={1.5} />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-text-muted text-xs">
                <Globe size={14} />
                <span>{t('nav.language')}</span>
              </div>
              <LanguageSwitcher compact />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
